import Lottie from "lottie-react";
import { useEffect, useState } from "react";
import AIThinkingAnimation from "static/images/AIThinking.json";
import { motion, AnimatePresence } from "framer-motion";
import { CrossCircle } from "components/icons/CrossCircle";
import { useDispatch } from "react-redux";
import {
  startThinking,
  showSad,
  resetToGeneral,
} from "store/slices/TaxAIResponse";

const loaderTextsForThinking = [
  "AI is thinking...",
  "AI is ideating...",
  "AI is crafting...",
];

const loaderTextsForSummary = [
  "Scanning for relevant information…",
  "Compiling insights and preparing response…",
];

// AIThinking controlled by variable (for AI channels)
export const AIThinkingVariable = ({ statusText }: { statusText?: string }) => {
  return (
    <div className="t-flex t-gap-1 t-items-center t-mt-2">
      {statusText ? (
        <div className="t-min-h-12 t-flex t-items-start t-gap-2 t-justify-center t-text-start t-text-text-60 t-text-subtext-sm">
          <Lottie className="t-w-5" animationData={AIThinkingAnimation} />
          <div className="t-whitespace-pre-line">{statusText}</div>
        </div>
      ) : (
        <>
          <Lottie className="t-w-5" animationData={AIThinkingAnimation} />
          <AILoader loaderTexts={loaderTextsForThinking} />
        </>
      )}
    </div>
  );
};

// AIThinking with timeout (for thread messages)
export const AIThinking = ({
  isSummaryThread = false,
}: {
  isSummaryThread?: boolean;
}) => {
  const [visible, setVisible] = useState(true);
  const dispatch = useDispatch();
  const loaderTexts = isSummaryThread
    ? loaderTextsForSummary
    : loaderTextsForThinking;
  useEffect(() => {
    dispatch(startThinking());

    const timeout = setTimeout(() => {
      setVisible(false);
      dispatch(showSad());

      setTimeout(() => {
        dispatch(resetToGeneral());
      }, 5000);
    }, 60000);

    return () => {
      clearTimeout(timeout);
    };
  }, [dispatch]);

  if (!visible) {
    return (
      <div className="t-text-red-50 t-text-subtext-sm t-flex t-items-center t-gap-1">
        <CrossCircle /> Oops... Sorry, something went wrong. Please try again.
      </div>
    );
  }

  return (
    <div className="t-flex t-gap-1 t-items-center">
      <Lottie className="t-w-5" animationData={AIThinkingAnimation} />
      <AILoader loaderTexts={loaderTexts} />
    </div>
  );
};

const AILoader = ({ loaderTexts }: { loaderTexts: string[] }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % loaderTexts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="t-h-12 t-flex t-items-center t-justify-center t-text-center t-text-text-30 t-text-sm">
      <AnimatePresence mode="wait">
        <motion.div
          key={loaderTexts[index]}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -5 }}
          transition={{ duration: 0.3 }}
        >
          <div className="t-text-text-60 t-text-subtext-sm">
            {loaderTexts[index]}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
